import { makeObservable } from 'mobx';

import type { TConstraints } from '@feathr/rachis';
import { isWretchError, wretch } from '@feathr/rachis';

import type { ICreativeAttributes } from './creatives_base';
import { Creative, CreativeClass } from './creatives_base';

// =============================================================================
// Referral Banner Creative Types
// =============================================================================

export type TReferralBannerCreative = ICreativeAttributes & {
  participant: string;
  width: number;
  height: number;
  destination_url: string;
  render_ad_tag: string;
  banner_hash?: string;
  banner_urls?: Array<Record<string, string>>;
};

// =============================================================================
// Referral Banner Creative Class
// =============================================================================

/**
 * ReferralBannerCreative handles referral campaign banner creatives.
 * These are participant-specific banners that can be synced from the backend.
 */
export class ReferralBannerCreative extends Creative<TReferralBannerCreative> {
  public readonly className = 'ReferralBannerCreative';

  public override get constraints(): TConstraints<TReferralBannerCreative> {
    return super.constraints;
  }

  constructor(attributes: Partial<TReferralBannerCreative>) {
    super(attributes);

    makeObservable(this);
  }

  public override getDefaults(): Partial<TReferralBannerCreative> {
    return {
      ...super.getDefaults(),
      _cls: CreativeClass.ReferralBanner,
    };
  }

  public override get name(): string {
    return this.get('name', '').trim() || 'Unnamed Creative';
  }

  /**
   * Gets a banner link by type and status.
   * @param type - The type of banner link to retrieve
   * @returns The URL of the banner link, or undefined if not found
   */
  public getBannerLink(type: string): string | undefined {
    const links = this.get('banner_urls') || [];
    const link = links.find((l) => l.type === type && l.status === 'done');
    if (link) {
      return link.url;
    }
    return undefined;
  }

  /**
   * Syncs the banner metadata from the backend.
   */
  public async sync(): Promise<void> {
    this.assertCollection(this.collection);
    const response = await wretch<Partial<TReferralBannerCreative>>(
      `${this.collection.url()}${this.id}/sync`,
      {
        method: 'GET',
        headers: this.collection.getHeaders(),
      },
    );

    if (isWretchError(response)) {
      throw response.error;
    }
    this.set({
      banner_urls: response.data.banner_urls,
    });
  }
}
